import { useEffect, useState } from 'react'
import { Autocomplete, Button, Card, CardActions, CardContent, Chip, Grid, TextField } from '@mui/material'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBasePath } from 'components/Providers'
import Header from 'components/Header'
import PersonSearch from 'components/PersonSearch'
import { save, destroy } from 'store/settings/service-access'
import { getCampus, getTypes, getRoles } from 'store/lookup'
import Confirm from 'components/Dialog'

const schema = yup.object().shape({
  type: yup.string().nullable(),
  roleIds: yup.array().min(1, 'At least one role is required'),
})

export default function SchoolAccessForm({ item, activeType }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()

  const campusList = useSelector(state => state.lookup.campus ?? [])
  const allRoles = useSelector(state => state.lookup.roles?.rows ?? state.lookup.roles ?? [])
  const districtTypes = useSelector(state =>
    state.lookup.types?.filter(tp => tp.category === 'districtType' && tp.code !== 'provider') ?? []
  )

  useEffect(() => { if (!campusList.length) dispatch(getCampus()) }, [])
  useEffect(() => { if (!allRoles.length) dispatch(getRoles()) }, [])
  useEffect(() => { if (!districtTypes.length) dispatch(getTypes()) }, [])

  const { control, handleSubmit, reset, setValue, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { person: null, type: null, campusIds: [], roleIds: [] },
  })

  useEffect(() => {
    if (!item || !allRoles.length) return
    const roles = item.personServiceRoles?.filter(r => !activeType || r.type === activeType) ?? []
    reset({
      type: roles[0]?.type ?? null,
      campusIds: [...new Set(roles.flatMap(r => r.campusId ?? []))],
      roleIds: roles.map(r => allRoles.find(ar => ar.name === r.role?.name)?.id).filter(Boolean),
    })
  }, [item, activeType, allRoles, reset])

  const selectedType = useWatch({ control, name: 'type' })
  const effectiveType = selectedType ?? (item ? activeType : null)

  const filteredCampuses = effectiveType
    ? campusList.filter(c => c.type === effectiveType)
    : campusList

  const filteredRoles = effectiveType
    ? allRoles.filter(r => !r.types?.length || r.types.includes(effectiveType))
    : allRoles

  const getServiceRoleId = () =>
    item?.personServiceRoles?.find(r => !activeType || r.type === activeType)?.id

  const onSubmit = async data => {
    const { person, ...rest } = data
    const personId = item ? item.id : person?.id
    const campusIds = rest.campusIds?.length ? rest.campusIds : null
    const payload = item
      ? { id: getServiceRoleId(), personId, ...rest, campusIds }
      : { personId, ...rest, campusIds }
    const res = await dispatch(save(payload))
    if (res.payload) navigate(basePath)
  }

  const onError = errors => console.error(errors)

  const [confirmOpen, setConfirmOpen] = useState(false)

  const onDelete = () => {
    dispatch(destroy({ id: getServiceRoleId() }))
    navigate(basePath)
  }

  return (
    <Card
      data-component='SchoolAccessForm'
      component='form'
      onSubmit={handleSubmit(onSubmit, onError)}
      noValidate
      sx={{ minHeight: '100%' }}
    >
      <Header
        title={item ? t('Edit School Access') : t('Add School Access')}
        close={() => navigate(basePath)}
        borderBottom
        small
      />
      <CardContent>
        <Grid container spacing={2}>

          <Grid item xs={12}>
            {item ? (
              <TextField
                label={t('Staff Member')}
                value={`${item.person?.lastName ?? item.lastName ?? ''}, ${item.person?.firstName ?? item.firstName ?? ''}`}
                disabled
                fullWidth
                size='small'
              />
            ) : (
              <Controller
                name='person'
                control={control}
                render={({ field, fieldState }) => (
                  <PersonSearch
                    label={t('Staff Member')}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='type'
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  size='small'
                  options={districtTypes}
                  getOptionLabel={option => option.name ?? ''}
                  value={districtTypes.find(tp => tp.code === field.value) ?? null}
                  disabled={!!item}
                  onChange={(_, value) => {
                    field.onChange(value?.code ?? null)
                    setValue('campusIds', [])
                    setValue('roleIds', [])
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('Type')}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='campusIds'
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={filteredCampuses}
                  getOptionLabel={option => option.name ?? ''}
                  value={filteredCampuses.filter(c => field.value?.includes(c.id))}
                  onChange={(_, value) => field.onChange(value.map(c => c.id))}
                  disabled={!item && !selectedType}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip key={option.id} label={option.name} size='small' {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('Campus')}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || t('Leave blank for full access')}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='roleIds'
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={filteredRoles}
                  getOptionLabel={option => option.name ?? ''}
                  value={filteredRoles.filter(r => field.value?.includes(r.id))}
                  onChange={(_, value) => field.onChange(value.map(r => r.id))}
                  disabled={!item && !selectedType}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip key={option.id} label={option.name} size='small' {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('User Role')}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>

        </Grid>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
        {item && (
          <Button variant='outlined' color='error' onClick={() => setConfirmOpen(true)}>
            {t('Delete')}
          </Button>
        )}
        <Button
          type='submit'
          variant='contained'
          disabled={!formState.isDirty}
        >
          {t('Save')}
        </Button>
      </CardActions>
      <Confirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        dialogTitle={t('Confirm Delete')}
        message={t('Are you sure to delete this access?')}
        onConfirm={onDelete}
      />

    </Card>
  )
}
